// PR-R5 — analytics report: a calendar heatmap of trips per day plus the key
// stats, for a vehicle or driver.
import { useQuery } from '@tanstack/react-query';
import { Loader2, BarChart3 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient } from '@/lib/api';

interface DailyRow { date: string; trips: number; m3: string; revenue: string }
interface Report { kind: 'vehicle' | 'driver'; subject: string; label: string; daily: DailyRow[]; stats: Record<string, number | string | null> }

const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

// Build calendar month grids spanning [from, to].
function monthsBetween(from: string, to: string): Array<{ y: number; m: number }> {
  const out: Array<{ y: number; m: number }> = [];
  let [y, m] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  while (y < ty || (y === ty && m <= tm)) { out.push({ y, m }); if (m === 12) { y++; m = 1; } else m++; }
  return out;
}

function Heatmap({ daily, from, to }: { daily: DailyRow[]; from: string; to: string }) {
  const byDate = new Map(daily.map((d) => [d.date, d.trips]));
  const max = Math.max(1, ...daily.map((d) => d.trips));
  const shade = (t: number) => {
    if (!t) return 'bg-muted/40 text-muted-foreground';
    const r = t / max;
    if (r > 0.75) return 'bg-emerald-600 text-white';
    if (r > 0.5) return 'bg-emerald-500 text-white';
    if (r > 0.25) return 'bg-emerald-400/80 text-emerald-950';
    return 'bg-emerald-300/60 text-emerald-950';
  };
  const months = monthsBetween(from.slice(0, 7) + '-01', to);
  const WD = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  return (
    <div className="space-y-4">
      {months.map(({ y, m }) => {
        const first = new Date(Date.UTC(y, m - 1, 1));
        const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const lead = first.getUTCDay();
        const cells: Array<number | null> = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
        return (
          <div key={`${y}-${m}`}>
            <div className="text-xs font-medium text-muted-foreground mb-1">{first.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</div>
            <div className="grid grid-cols-7 gap-1">
              {WD.map((w, i) => <div key={i} className="text-[10px] text-center text-muted-foreground">{w}</div>)}
              {cells.map((day, i) => {
                if (day == null) return <div key={i} />;
                const iso = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const trips = byDate.get(iso) ?? 0;
                return (
                  <div key={i} className={`aspect-square rounded-md flex flex-col items-center justify-center text-[10px] ${shade(trips)}`} title={`${iso}: ${trips} trip${trips === 1 ? '' : 's'}`}>
                    <span className="opacity-70 leading-none">{day}</span>
                    {trips > 0 && <span className="font-bold leading-none mt-0.5">{trips}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const STAT_LABELS: Record<string, string> = {
  totalTrips: 'Total trips', totalM3: 'm³ delivered', totalRevenue: 'Revenue SAR', totalKm: 'Total km',
  activeDays: 'Active days', tripsPerActiveDay: 'Trips / active day', dieselSar: 'Diesel SAR',
  dieselSarPerKm: 'Diesel SAR/km', litersPer100km: 'L / 100km', maintSar: 'Maintenance SAR',
  costPerKm: 'Cost SAR/km', fuelFills: 'Fuel fills', maintJobs: 'Maint jobs',
  grossPay: 'Gross pay SAR', netPay: 'Net pay SAR', advances: 'Advances SAR', payModel: 'Pay model',
};
const STAT_ORDER = ['totalTrips', 'tripsPerActiveDay', 'activeDays', 'totalM3', 'totalRevenue', 'totalKm', 'dieselSar', 'dieselSarPerKm', 'litersPer100km', 'maintSar', 'costPerKm', 'fuelFills', 'maintJobs', 'grossPay', 'netPay', 'advances', 'payModel'];

export function ReportSheet({ kind, id, from, to, onClose }: { kind: 'vehicle' | 'driver'; id: string; from: string; to: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['report', kind, id, from, to],
    queryFn: async () => (await apiClient.get<Report>(`/api/v1/project-reports/${kind}/${id}/report`, { params: { scope: 'neom', from, to } })).data,
  });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> {data?.subject ?? 'Report'} · {data?.label ?? ''}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-5" data-report>
          {isLoading || !data ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {STAT_ORDER.filter((k) => data.stats[k] != null).map((k) => (
                  <div key={k} className="rounded-lg border border-border bg-card p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{STAT_LABELS[k] ?? k}</div>
                    <div className="text-lg font-semibold tabular-nums mt-0.5">{typeof data.stats[k] === 'number' ? fmt(data.stats[k] as number) : String(data.stats[k]).replace(/_/g, ' ')}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Trips per day</div>
                {data.daily.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No trips in this period.</div>
                ) : (
                  <Heatmap daily={data.daily} from={from} to={to} />
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
